import type {CadEntityKind} from '../document';
import {
    type ChangeSet,
    type EntityPatch,
    type EntityPatchMetadata,
    type HistoryEntitySnapshot,
    resolveEntityPatchOperation,
} from './types';

/**
 * 实体快照捕获输入。
 */
export interface CaptureEntitySnapshotInput<TSnapshot = unknown> {
    /** 实体类型。 */
    readonly entityKind: CadEntityKind;
    /** 实体稳定 ID。 */
    readonly entityId: string;
    /** domain adapter ID。 */
    readonly adapterId: string;
    /** 实体快照。 */
    readonly snapshot: HistoryEntitySnapshot<TSnapshot>;
    /** 补丁元数据。 */
    readonly metadata?: EntityPatchMetadata;
}

/**
 * ChangeSet 构建器。
 */
export interface ChangeSetBuilder {
    /**
     * 捕获 before 快照。
     *
     * @param input 捕获输入。
     */
    readonly captureBefore: (input: CaptureEntitySnapshotInput) => void;
    /**
     * 捕获 after 快照。
     *
     * @param input 捕获输入。
     */
    readonly captureAfter: (input: CaptureEntitySnapshotInput) => void;
    /**
     * 构建变更集。
     *
     * @returns 变更集。
     */
    readonly build: () => ChangeSet;
    /**
     * 清空构建器状态。
     */
    readonly clear: () => void;
}

/**
 * 构建器内部记录。
 */
interface ChangeSetBuilderRecord {
    /** 实体类型。 */
    readonly entityKind: CadEntityKind;
    /** 实体稳定 ID。 */
    readonly entityId: string;
    /** domain adapter ID。 */
    readonly adapterId: string;
    /** before 快照。 */
    before?: HistoryEntitySnapshot;
    /** after 快照。 */
    after?: HistoryEntitySnapshot;
    /** 补丁元数据。 */
    metadata?: EntityPatchMetadata;
}

/**
 * 创建实体补丁 key。
 *
 * @param input 实体快照捕获输入。
 * @returns 补丁 key。
 */
function createPatchKey(input: Pick<CaptureEntitySnapshotInput, 'adapterId' | 'entityId'>): string {
    return `${input.adapterId}:${input.entityId}`;
}

/**
 * 根据补丁列表创建变更集。
 *
 * @param patches 实体补丁列表。
 * @returns 变更集。
 */
export function createChangeSet(patches: readonly EntityPatch[]): ChangeSet {
    return {
        patches,
        createdEntityIds: patches
            .filter((patch) => patch.operation === 'create')
            .map((patch) => patch.entityId),
        updatedEntityIds: patches
            .filter((patch) => patch.operation === 'update')
            .map((patch) => patch.entityId),
        deletedEntityIds: patches
            .filter((patch) => patch.operation === 'delete')
            .map((patch) => patch.entityId),
    };
}

/**
 * 将构建器记录转换为实体补丁。
 *
 * @param record 构建器记录。
 * @returns 实体补丁。
 */
function createPatchFromRecord(record: ChangeSetBuilderRecord): EntityPatch {
    if (!record.before) {
        throw new Error(`实体 ${record.entityId} 缺少 before 快照。`);
    }

    if (!record.after) {
        throw new Error(`实体 ${record.entityId} 缺少 after 快照。`);
    }

    return {
        entityKind: record.entityKind,
        entityId: record.entityId,
        adapterId: record.adapterId,
        operation: resolveEntityPatchOperation(record.before, record.after),
        before: record.before,
        after: record.after,
        metadata: record.metadata,
    };
}

/**
 * 写入捕获记录。
 *
 * @param recordMap 记录表。
 * @param input 捕获输入。
 * @param side 捕获位置。
 */
function captureSnapshot(
    recordMap: Map<string, ChangeSetBuilderRecord>,
    input: CaptureEntitySnapshotInput,
    side: 'before' | 'after',
): void {
    const key = createPatchKey(input);
    const existingRecord = recordMap.get(key);
    const record = existingRecord ?? {
        entityKind: input.entityKind,
        entityId: input.entityId,
        adapterId: input.adapterId,
    };

    if (existingRecord) {
        if (existingRecord.entityKind !== input.entityKind) {
            throw new Error(`实体 ${input.entityId} 的类型捕获不一致。`);
        }

        if (existingRecord.adapterId !== input.adapterId) {
            throw new Error(`实体 ${input.entityId} 的 adapter 捕获不一致。`);
        }
    }

    if (side === 'before' && record.before) {
        return;
    }

    record[side] = input.snapshot;
    record.metadata = {
        ...record.metadata,
        ...input.metadata,
    };
    recordMap.set(key, record);
}

/**
 * 创建 ChangeSet 构建器。
 *
 * @returns ChangeSet 构建器。
 */
export function createChangeSetBuilder(): ChangeSetBuilder {
    const recordMap = new Map<string, ChangeSetBuilderRecord>();

    return {
        captureBefore(input) {
            captureSnapshot(recordMap, input, 'before');
        },
        captureAfter(input) {
            captureSnapshot(recordMap, input, 'after');
        },
        build() {
            return createChangeSet(Array.from(recordMap.values()).map(createPatchFromRecord));
        },
        clear() {
            recordMap.clear();
        },
    };
}
